import { useParams } from "react-router";

import { EstimateTrackerPath, pageTitleForEstimateTrackerPath } from "../EstimateTrackerRoutes";

export interface EstimateTrackingInternalLinkProps {
  trackerPath: EstimateTrackerPath;
  children: React.FC<{ targetPath: string; targetPageTitle: string }>;
}

/**
 * Helper component to render a link to a estimate tracking page, specified by the `trackerPath` prop. The child component
 * is passed a `targetPath` prop that contains the full path to the estimate tracking page, which is constructed
 * based on the current estimate number in the URL route params.
 *
 * This child component is also passed a `targetPageTitle` prop, which is the title of the target page.
 *
 * For example, if the current route is `/estimate/Q-1234/details` and `trackerPath` is `bom-hierarchy`,
 * the child component will receive a `targetPath` prop as `/estimate/Q-1234/bom-hierarchy`.
 *
 * If this component is used on a page that does not have a `estimateNumber` route param, it will throw an error.
 *
 * @example
 * ```tsx
 * <EstimateTrackingInternalLink trackerPath="bom-hierarchy">
 *   {({ targetPath, targetPageTitle }) => (
 *     <SOSEntityLineItemHashLink
 *       fromHash={row.id}
 *       urlPath={targetPath}
 *       tooltipContent={`View on '${targetPageTitle}' page`}
 *       type="item"
 *       id={item.id}
 *       lineNumber={0}
 *       label={item.name}
 *     />
 *   )}
 * </EstimateTrackingInternalLink>
 * ```
 */
export const EstimateTrackingInternalLink: React.FC<EstimateTrackingInternalLinkProps> = ({
  trackerPath,
  children: ChildComponent,
}) => {
  const routeParams = useParams();
  const estimateNumber = routeParams["estimateNumber"];
  if (!estimateNumber) throw new Error("No estimate number found in URL route params");
  const targetPath = `/estimate/${estimateNumber}/${trackerPath}`;
  const targetPageTitle = pageTitleForEstimateTrackerPath[trackerPath];
  return <ChildComponent targetPath={targetPath} targetPageTitle={targetPageTitle} />;
};
